import React, { useState, useMemo, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { GoChevronLeft, GoChevronRight } from "react-icons/go";

// custom components
import { Card, CustomSpinner } from "_atoms";
import { StarRating, TableHeader, TableValue } from "_molecules";
import { ClickableTable } from '_organisms'

// bootstrap
import { Row, Col, Button } from 'react-bootstrap';

// redux
import { useSelector, useDispatch } from 'react-redux';
import { selectUserDb } from '_redux/slices/main-screen/user';
import { setUser } from '_redux/slices/profile/user-profile';
import { selectReviewsStatus, getReviewsList, selectReviewsPageList, modifyReviewListSkip, resetReviewListSkip, selectReviewListSkip, selectAirportBase64Logo, resetReviewReportFlags, selectReviewListReportStatus } from '_redux/slices/analytics/reviews';

import 'react-datetime/css/react-datetime.css';
import { Title as TremorTitle, Subtitle, Flex, Toggle, ToggleItem, DateRangePicker } from '@tremor/react';

import { Colors } from '_styles'
import { makeExcel } from '_utils/makeExcel';

//date 
import moment from 'moment';

// function to get inital date value
function getDateOneMonthAgo(currentDate) {
    // Get the current timestamp
    const currentTimestamp = currentDate.getTime();

    // Calculate the timestamp for 30 days ago (30 * 24 * 60 * 60 * 1000 milliseconds)
    const thirtyDaysAgoTimestamp = currentTimestamp - 2592000000;

    // Create a new date object from the timestamp for 30 days ago
    const thirtyDaysAgo = new Date(thirtyDaysAgoTimestamp);

    return thirtyDaysAgo;
}

const TableCount = (props) => {

    // counts
    const skipObj = useSelector(state => state.reviews.reviewListSkip)
    const skip = skipObj[props.typeFilter]

    return (
        <Subtitle>
            {`${skip} - ${props.countWithFilter - skip < 50 ? props.countWithFilter : skip + 50} of ${props.countWithFilter}`}
        </Subtitle>
    );
};

const PaginationClicker = (props) => {
    // filters
    const dispatch = useDispatch();
    const skipObj = useSelector(state => state.reviews.reviewListSkip)
    const skip = skipObj[props.typeFilter]

    return (
        <Row>
            <Col className='mt-3'>
                <Row style={{ display: 'flex' }}>


                    {/* buttons */}
                    <div style={{ display: 'flex' }}>
                        {/* count */}
                        <div style={{ marginRight: 22 }}>
                            <TableCount typeFilter={props.typeFilter} countWithFilter={props.countWithFilter} />
                        </div>

                        <div style={{ display: 'flex' }}>
                            <GoChevronLeft
                                onClick={() => dispatch(modifyReviewListSkip({ change: -50, typeFilter: props.typeFilter }))}
                                size={24}
                                style={{
                                    cursor: "pointer",
                                    opacity: skip - 50 < 0 ? 0.3 : 1.0,
                                    pointerEvents: skip - 50 < 0 ? "none" : "auto"
                                }}
                                color={Colors.PRM_LIGHT_BLUE}
                            />
                            <GoChevronRight
                                onClick={() => { dispatch(modifyReviewListSkip({ change: 50, typeFilter: props.typeFilter })) }}
                                size={24}
                                style={{
                                    marginLeft: 28,
                                    cursor: "pointer",
                                    opacity: skip + 50 > props.countWithFilter ? 0.3 : 1.0,
                                    pointerEvents: skip + 50 > props.countWithFilter ? "none" : "auto"
                                }}
                                color={Colors.PRM_LIGHT_BLUE}
                            />
                        </div>
                    </div>
                </Row>
            </Col>
        </Row>
    );
};


const ReviewList = () => {

    // nav
    const history = useHistory();

    // redux
    const dispatch = useDispatch();
    const dbUser = useSelector(selectUserDb)
    const airportBase64Logo = useSelector(selectAirportBase64Logo)

    // date filter
    const currentDate = new Date()
    currentDate.setMinutes(currentDate.getMinutes() + 30)
    currentDate.setMinutes(0, 0, 0)
    const [dateRange, setDateRange] = useState([
        getDateOneMonthAgo(currentDate),
        currentDate
    ]);

    // table filter
    const [allBookings, setAllBookings] = useState(false)

    // flight type filter
    const [typeFilter, setTypeFilter] = useState('All')
    const [exportingFile, setExportingFile] = useState(false)

    const skipObj = useSelector(selectReviewListSkip)
    const skip = skipObj?.[typeFilter]

    // redux
    const reviewsListObj = useSelector(selectReviewsPageList(dbUser, "", "All", typeFilter, skip))   // contains reviews for table
    const reviewsList = reviewsListObj?.data
    const countWithFilter = reviewsListObj?.count
    const status = useSelector(selectReviewsStatus)                                 // contains status for table fetch
    const reviewsReportStatus = useSelector(selectReviewListReportStatus)

    //#region review table functions and columns
    const columns = useMemo(() => {
        const cols = [
            {
                Header: <TableHeader title={''} />,
                accessor: "count",
                Cell: ({ cell: { value } }) => <p>{value}</p>
            },
            {
                Header: <TableHeader title={'Name'} />,
                accessor: "name",
                Cell: ({ cell: { value } }) => <TableValue title={value} />
            },
            {
                Header: <TableHeader title={'Flight Date'} />,
                accessor: "date",
                Cell: ({ cell: { value } }) => <TableValue title={value} />
            },
            {
                Header: <TableHeader title={'Flight'} />,
                accessor: "flightNumber",
                Cell: ({ cell: { value } }) => <TableValue title={value} />
            },
            {
                Header: (
                    <TableHeader
                        title={
                            dbUser?.details?.type === 'AIRLINE'
                                ? 'Boarding Assistance'
                                : 'Assistance'
                        }
                    />
                ),
                accessor:
                    dbUser?.details?.type === 'AIRLINE'
                        ? 'boarding'
                        : 'assistanceQuality',
                Cell: ({ cell: { value } }) => <StarRating stars={value} />
            },
            {
                Header: (
                    <TableHeader
                        title={
                            dbUser?.details?.type === 'AIRLINE'
                                ? 'Onboard Assistance'
                                : 'Staff Assistance'
                        }
                    />
                ),
                accessor:
                    dbUser?.details?.type === 'AIRLINE'
                        ? 'onboard'
                        : 'staffAssistance',
                Cell: ({ cell: { value } }) => <StarRating stars={value} />
            },
            {
                Header: <TableHeader title={'Comments'} />,
                accessor: "comments",
                Cell: ({ cell: { value } }) => (
                    <TableValue title={value} stars={false} overall={true} />
                )
            }
        ];

        if (dbUser?.details.type === 'AIRPORT') {
            cols.push({
                Header: <TableHeader title={'Waiting Time'} />,
                accessor: 'waitingTime',
                Cell: ({ cell: { value } }) => <TableValue title={value} />
            });
        }

        return cols;
    }, [dbUser]);

    //#endregion



    // fetch handler for review table
    useEffect(() => {
        dispatch(resetReviewListSkip())
        // get reviews info
        if (dbUser) {
            const startDate = moment(new Date(dateRange[0])).format().split("T")[0];
            const endDate = moment(new Date(dateRange[1] ? dateRange[1] : dateRange[0])).format().split("T")[0];
            const params = {
                'iata': dbUser.details.authority,
                'skip': 0,
                'airline': dbUser.details.type == 'AIRLINE' ? true : false,
                startDate,
                endDate,
                'allBookings': allBookings
            }
            dispatch(getReviewsList(params))
        }
    }, [dbUser, dateRange, allBookings]);

    useEffect(() => {
        if (reviewsReportStatus === "succeeded" || reviewsReportStatus === "failed") dispatch(resetReviewReportFlags())
    }, [reviewsReportStatus]);



    const handleClick = (i) => {
        let row = reviewsList[i]
        console.log(row, "row.....")
        dispatch(setUser(row.userId))
        history.push('/user-profile')
    }
    const processColumnData = (columnsData) => {
        return columnsData.map((col) => {
            return {
                name: col.name,
                flightDate: col.flightDate,
                flightNumber: col.flightNumber,
                assistanceQuality: col.assistanceQuality,
                staffAssistance: col.staffAssistance,
                comments: col.comments,
            }
        })
    }
    //this will generate report with current rows, not all records
    const exportReviewListReport = async () => {
        setExportingFile(true)
        const endDate = dateRange[1] ? dateRange[1].toISOString().split('T')[0] : dateRange[0].toISOString().split('T')[0]
        const reportColumns = ["Name", "Flight date", "Flight", "Assistance quality", "Staff assistance", "comments"]
        const exportedFileName = `Review list_${dbUser?.details.authority}_${dateRange[0].toISOString().split('T')[0]}_${endDate}`
        let data = {};

        data.fileName = exportedFileName;
        data.workSheetName = [exportedFileName];
        data.columnTitles = [reportColumns];
        data.columnWidth = [[30, 20, 20, 25, 25, 60]];
        const processedData = processColumnData(reviewsList)
        data.rows = [processedData]
        await makeExcel(data, dbUser?.details.authority, airportBase64Logo);
        setExportingFile(false);
    }

    return (
        <div style={{ paddingBottom: 18 }}>

            {/* flight type tab selector + all review selector + date range picker */}
            <Row className='mt-3'>
                <Flex>
                    <Col xs={3}>
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'flex-start', marginRight: 12 }} className='mt-3'>
                                <Toggle color="zinc" defaultValue={allBookings} onValueChange={(value) => { dispatch(resetReviewListSkip()), setAllBookings(value) }} style={{ marginLeft: 12 }}>
                                    <ToggleItem value={false} text={"By Flight Date"} />
                                    <ToggleItem value={true} text={"All Reviews"} />
                                </Toggle>
                            </div>
                        </div>
                    </Col>
                    <Col>
                        {dbUser.details.type == 'AIRLINE' ? '' :
                            <div style={{ display: 'flex', justifyContent: 'flex-start' }} className='mt-3'>
                                <Toggle color="zinc" defaultValue={typeFilter} onValueChange={(value) => setTypeFilter(value)}>
                                    <ToggleItem value={'All'} text={"All Flights"} />
                                    <ToggleItem value={'Departures'} text={"Departures"} />
                                    <ToggleItem value={'Arrivals'} text={"Arrivals"} />
                                </Toggle>
                            </div>
                        }
                    </Col>
                    <Col xs={5}>
                        {false ? '' :
                            !allBookings &&
                            <DateRangePicker
                                disabled={false}
                                onValueChange={(value) => { setDateRange(value) }}
                                value={dateRange}
                                enableDropdown={true}
                                maxWidth="max-w-lg"
                                dropdownPlaceholder={'Last 30 Days'}
                                marginTop='mt-3'
                            />
                        }
                    </Col>
                </Flex>
            </Row>

            {/* Review Table */}
            <Row className='mt-4'>

                <Card>

                    <div style={{ marginLeft: 12, marginBottom: 8 }}>
                        <TremorTitle>Review List</TremorTitle>
                        <Subtitle>{'View detailed reviews surveyed from passengers using PRM Assist to travel'}</Subtitle>
                    </div>


                    {/* table */}
                    <div style={{ overflowY: 'scroll', height: '40em', }}>
                        {
                            <ClickableTable
                                columns={columns}
                                data={reviewsList ? reviewsList : []}
                                handleClick={(i) => { handleClick(i) }}
                                status={status}
                                clickable={true}
                            />
                        }
                    </div>

                    {/* pagination clicker + total counts */}
                    {
                        status == "loading" ? '' :
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 15px', marginTop: '1rem' }}>

                                {/* Left side: Export + Total Reviews */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', cursor: exportingFile || !reviewsList?.length ? "not-allowed" : "pointer" }}>
                                    <Button
                                        variant="outline-primary"
                                        onClick={exportReviewListReport}
                                        disabled={exportingFile || !reviewsList?.length}
                                        style={{ width: 150, marginTop: "10px" }}
                                    >
                                        {exportingFile ? <CustomSpinner /> : "Export"}
                                    </Button>

                                </div>

                                {/* Right side: Pagination */}
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <PaginationClicker typeFilter={typeFilter} countWithFilter={countWithFilter} />
                                </div>

                            </div>
                    }

                </Card>

            </Row>
        </div>
    );
}

export default ReviewList;
